import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import supportingDocumentTypeService from '../../service/supportingDocumentType.service';
import addNotification from '../../utils/addNotification';
import { generateAutocompleteItems } from '../../utils/helper';
import Yup from '../../utils/yup';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import SupportingDocumentTypeFields from './SupportingDocumentTypeFields';
import SupportingDocumentTypeList from './SupportingDocumentTypeList';
import SupportingDocumentTypeTabs from './SupportingDocumentTypeTabs';

const defaultValues = {
  id: null,
  name: '',
  note: '',
  generated: true,
  prefix: '',
  source: null,
};

const SupportingDocumentTypePage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit, getCanDelete } = useMenus();
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState(defaultValues);
  const [viewField, setViewField] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refetchTable, setRefetchTable] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  useEffect(() => {
    getMenus();
    getList();
    masterDataService.languageToForm().then((data) => {
      setLanguageOptions(generateAutocompleteItems(data, ['code', 'name'], 'code'));
    });
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('supportingDocumentType');
    setCanEdit(getCanEdit(submenuId));
    setCanDelete(getCanDelete(submenuId));
  }, [menus]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required(),
    note: Yup.string().nullable(),
    generated: Yup.boolean().required(),
    source: Yup.string().nullable(),
    prefix: Yup.string().when('generated', {
      is: true,
      then: (schema) => schema.trim().required(),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const getList = async () => {
    const data = await supportingDocumentTypeService.getItems();
    setRows(Array.isArray(data) ? data : data?.rows ?? []);
  };

  const loadItem = async (id, edit = false) => {
    const data = await supportingDocumentTypeService.getItem(id);
    setSelectedValues({
      id: data.id,
      name: data.name ?? '',
      note: data.note ?? '',
      generated: Boolean(data.generated),
      prefix: data.generated ? data.prefix ?? '' : '',
      source: data.generated ? null : data.source ?? null,
    });
    setReports(data.supportingDocumentTypeReport ?? []);
    setRowSelectionModel([data.id]);
    setAddField(true);
    setViewField(!edit);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (values.generated && reports.length === 0) {
      addNotification({
        content: 'Generált típushoz legalább egy .rptdesign riportmelléklet kötelező!',
        type: NotificationType.ERROR,
      });
      setSubmitting(false);
      return;
    }

    const invalidReport = reports.find((report) => (report.extension ?? '').toLowerCase() !== '.rptdesign');
    if (values.generated && invalidReport) {
      addNotification({
        content: `Csak .rptdesign riport használható: ${invalidReport.name}`,
        type: NotificationType.ERROR,
      });
      setSubmitting(false);
      return;
    }

    setIsSaving(true);
    try {
      const savedReports = values.generated ? reports : [];
      const payload = {
        ...(values.id ? { id: values.id } : {}),
        name: values.name.trim(),
        note: values.note?.trim() || null,
        generated: values.generated,
        prefix: values.generated ? values.prefix?.trim() || null : null,
        ...(values.generated ? {} : { source: values.source || null }),
        supportingDocumentTypeReport: savedReports.map((report) => ({
          ...(report.isDraft ? {} : { id: report.id }),
          ...(report.isDraft ? {} : { supportingDocumentTypeId: values.id }),
          name: report.name,
          label: report.label ?? null,
          ...(report.isDraft ? {} : { path: report.path }),
          extension: report.extension,
          langCode: report.langCode ?? null,
          isNew: Boolean(report.isDraft),
        })),
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));

      for (const report of savedReports.filter((report) => report.isDraft)) {
        if (report.file instanceof File) {
          formData.append('files', report.file);
        }
      }

      const data = values.id
        ? await supportingDocumentTypeService.updateItem(formData)
        : await supportingDocumentTypeService.createItem(formData);

      const savedId = data?.id ?? values.id;
      if (!savedId) return;

      if (!values.id) {
        setSelectedValues({ ...values, id: savedId });
      }

      addNotification({
        content: values.id ? 'Sikeres módosítás!' : 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });

      await loadItem(savedId);
      setRefetchTable(true);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete?.id || isSaving) return;
    setIsSaving(true);
    try {
      await supportingDocumentTypeService.deleteItem(itemToDelete.id);
      addNotification({ content: 'Sikeres törlés!', type: NotificationType.WARNING });
      setSelectedValues(defaultValues);
      setReports([]);
      setViewField(true);
      setAddField(true);
      setItemToDelete(null);
      setRowSelectionModel([]);
      setRefetchTable(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFilesAdded = (files) => {
    const existingNames = new Set(reports.map((report) => report.name));
    const duplicateFile = files.find((file) => existingNames.has(file.name));
    const invalidFile = files.find((file) => !file.name.toLowerCase().endsWith('.rptdesign'));

    if (duplicateFile) {
      addNotification({
        content: `Már szerepel ilyen nevű fájl a listában: ${duplicateFile.name}`,
        type: NotificationType.ERROR,
      });
      return false;
    }

    if (invalidFile) {
      addNotification({
        content: `Csak .rptdesign riport tölthető fel: ${invalidFile.name}`,
        type: NotificationType.ERROR,
      });
      return false;
    }

    const newReports = files.map((file) => ({
      id: uuidv4(),
      supportingDocumentTypeId: selectedValues.id,
      file,
      name: file.name,
      label: '',
      path: '',
      extension: file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '',
      langCode: null,
      isDraft: true,
    }));

    setReports((currentReports) => [...currentReports, ...newReports]);
    return true;
  };

  const handleDeleteReport = async (report) => {
    if (!canEdit || !report?.id || isSaving) return;

    if (report.isDraft) {
      setReports((currentReports) => currentReports.filter((item) => item.id !== report.id));
      return;
    }

    setIsSaving(true);
    try {
      await supportingDocumentTypeService.deleteReport(report.id);
      addNotification({ content: 'Sikeres törlés!', type: NotificationType.WARNING });
      await loadItem(selectedValues.id, true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <FormBase
        name="SupportingDocumentType"
        leftPanelContent={
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultValues);
                setReports([]);
                setRowSelectionModel([]);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit || isSaving}
              modClick={() => setViewField(false)}
              modDisabled={!viewField || !selectedValues.id || !canEdit || isSaving}
              deleteClick={() => setItemToDelete(selectedValues)}
              deleteDisabled={!viewField || !selectedValues.id || !canDelete || isSaving}
            />
            <Formik
              initialValues={selectedValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ resetForm, isSubmitting }) => (
                <Form>
                  <SupportingDocumentTypeFields viewField={viewField} />
                  <SupportingDocumentTypeTabs
                    reports={reports}
                    setReports={setReports}
                    languageOptions={languageOptions}
                    canEdit={canEdit}
                    isSaving={isSaving}
                    disabled={viewField}
                    onFilesAdded={handleFilesAdded}
                    onDeleteReport={handleDeleteReport}
                  />
                  <FormButtonPanelUI
                    onCancelClick={() => {
                      if (selectedValues.id) {
                        loadItem(selectedValues.id);
                      } else {
                        setReports([]);
                      }
                      setViewField(true);
                      setAddField(true);
                      resetForm();
                    }}
                    disabled={(viewField && addField) || isSubmitting || isSaving}
                  />
                </Form>
              )}
            </Formik>
          </div>
        }
        rightPanelContent={
          <SupportingDocumentTypeList
            rows={rows}
            refetch={{ getList, refetchTable, setRefetchTable }}
            onSelect={(id) => {
              if (viewField && !isSaving) loadItem(id);
            }}
            isSaving={isSaving}
            viewField={viewField}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <DeleteDialogUI
        show={Boolean(itemToDelete)}
        onHide={() => setItemToDelete(null)}
        handleRemoveElement={handleDelete}
        disabled={isSaving}
      />
    </>
  );
};

export default SupportingDocumentTypePage;
